import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fundAPI, fieldCatalogAPI } from '../../services/api';
import SOATemplateDesigner from './SOATemplateDesigner';
import { Plus, Trash2, Download, FileText, Copy, Check, X, Save, ChevronDown, ChevronRight, Search, Upload, Eye } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FundDocumentTemplates = ({ fundId, canEdit }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fieldCatalog, setFieldCatalog] = useState([]);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [copiedField, setCopiedField] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({});

  // PDF viewer state
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfFieldPlacements, setPdfFieldPlacements] = useState([]);
  const [placingField, setPlacingField] = useState(null);
  const [savingPlacements, setSavingPlacements] = useState(false);
  const pdfContainerRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [fundId]);

  const loadData = async () => {
    try {
      const [tmplRes, catalogRes] = await Promise.all([
        fundAPI.getTemplates(fundId),
        fieldCatalogAPI.getAll(fundId),
      ]);
      setTemplates(tmplRes.data);
      setFieldCatalog(catalogRes.data);
      // Expand all sections by default
      const expanded = {};
      catalogRes.data.forEach(s => { expanded[`${s.module}-${s.section}`] = true; });
      setExpandedSections(expanded);
    } catch (err) { console.error('Failed to load templates:', err); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadName.trim()) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', uploadName.trim());
      formData.append('file', uploadFile);
      await fundAPI.createTemplate(fundId, formData);
      const res = await fundAPI.getTemplates(fundId);
      setTemplates(res.data);
      setUploadName('');
      setUploadFile(null);
      setShowUploadForm(false);
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await fundAPI.deleteTemplate(fundId, templateId);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      if (selectedTemplate?.id === templateId) { setSelectedTemplate(null); setPdfUrl(null); }
    } catch (err) { alert('Failed to delete'); }
  };

  const handleDownload = async (template) => {
    try {
      const res = await fundAPI.downloadTemplate(template.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = template.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert('Download failed'); }
  };

  const handleSelectTemplate = async (template) => {
    setSelectedTemplate(template);
    setPdfFieldPlacements(template.pdf_field_placements || []);
    setCurrentPage(1);
    if (template.file_type === 'pdf') {
      try {
        const res = await fundAPI.previewTemplate(template.id);
        const url = URL.createObjectURL(res.data);
        setPdfUrl(url);
      } catch { setPdfUrl(null); }
    } else {
      setPdfUrl(null);
    }
  };

  const copyPlaceholder = (fieldKey) => {
    const tag = `{{${fieldKey}}}`;
    navigator.clipboard.writeText(tag);
    setCopiedField(fieldKey);
    setTimeout(() => setCopiedField(''), 2000);
  };

  const handlePdfClick = useCallback((e) => {
    if (!placingField || !pdfContainerRef.current) return;
    const rect = pdfContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const newPlacement = {
      field_key: placingField.key,
      field_label: placingField.label,
      page: currentPage,
      x, y,
      width: 150, height: 20, font_size: 10,
    };
    setPdfFieldPlacements(prev => [...prev, newPlacement]);
    setPlacingField(null);
  }, [placingField, currentPage]);

  const removePdfPlacement = (idx) => {
    setPdfFieldPlacements(prev => prev.filter((_, i) => i !== idx));
  };

  const savePdfPlacements = async () => {
    if (!selectedTemplate) return;
    setSavingPlacements(true);
    try {
      await fundAPI.updateTemplate(fundId, selectedTemplate.id, {
        pdf_field_placements: pdfFieldPlacements,
        placeholders: pdfFieldPlacements.map(p => p.field_key),
      });
      const res = await fundAPI.getTemplates(fundId);
      setTemplates(res.data);
      const updated = res.data.find(t => t.id === selectedTemplate.id);
      if (updated) setSelectedTemplate(updated);
    } catch (err) { alert('Failed to save placements'); }
    finally { setSavingPlacements(false); }
  };

  const saveWordPlaceholders = async (template, placeholders) => {
    try {
      await fundAPI.updateTemplate(fundId, template.id, { placeholders });
      const res = await fundAPI.getTemplates(fundId);
      setTemplates(res.data);
    } catch { /* silent */ }
  };

  // Filter field catalog by search
  const filteredCatalog = fieldCatalog.map(section => ({
    ...section,
    fields: section.fields.filter(f =>
      f.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.key.toLowerCase().includes(searchTerm.toLowerCase())
    ),
  })).filter(s => s.fields.length > 0);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) return <div className="text-center py-8 text-gray-500">Loading templates...</div>;

  return (
    <div data-testid="document-templates-tab">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Document Templates</h3>
          <p className="text-sm text-gray-500 mt-1">Upload Word or PDF templates and map dynamic fields for mail-merge.</p>
        </div>
        {canEdit && (
          <button onClick={() => setShowUploadForm(true)} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="upload-template-btn">
            <Plus size={16} className="mr-2" /> Upload Template
          </button>
        )}
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <form onSubmit={handleUpload} className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-testid="upload-template-form">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Upload New Template</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Template Name *</label>
              <input type="text" value={uploadName} onChange={e => setUploadName(e.target.value)} required
                placeholder="e.g. Drawdown Notice" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="template-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">File (.docx or .pdf) *</label>
              <input type="file" accept=".docx,.pdf,.doc" onChange={e => setUploadFile(e.target.files[0])}
                required className="w-full border rounded-lg px-3 py-2 text-sm mt-1 bg-white" data-testid="template-file-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => { setShowUploadForm(false); setUploadFile(null); setUploadName(''); }} className="px-3 py-1.5 text-sm border rounded-lg">Cancel</button>
            <button type="submit" disabled={uploading} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50" data-testid="save-template-btn">
              <Upload size={14} className="mr-1 inline" /> {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      )}

      {/* Templates List */}
      {templates.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No document templates yet. Upload your first template above.</p>
      ) : (
        <div className="space-y-3 mb-6">
          {templates.map(t => (
            <div key={t.id} className={`border rounded-lg p-4 flex items-center justify-between transition-colors cursor-pointer hover:border-blue-300 ${selectedTemplate?.id === t.id ? 'border-blue-400 bg-blue-50/40' : 'border-gray-200'}`}
              onClick={() => handleSelectTemplate(t)} data-testid={`template-card-${t.id}`}>
              <div className="flex items-center gap-3">
                <FileText size={24} className={`${t.file_type === 'pdf' ? 'text-red-500' : 'text-blue-500'}`} />
                <div>
                  <h4 className="font-semibold text-gray-900">{t.name}</h4>
                  <p className="text-xs text-gray-500">{t.original_filename} &middot; {t.file_type.toUpperCase()} &middot; {(t.file_size / 1024).toFixed(1)} KB</p>
                  {(t.placeholders?.length > 0 || t.pdf_field_placements?.length > 0) && (
                    <p className="text-xs text-blue-600 mt-0.5">{t.pdf_field_placements?.length || t.placeholders?.length || 0} fields mapped</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                <button onClick={() => handleDownload(t)} className="p-2 text-gray-400 hover:text-blue-600" title="Download" data-testid={`download-template-${t.id}`}>
                  <Download size={16} />
                </button>
                {canEdit && (
                  <button onClick={() => handleDelete(t.id)} className="p-2 text-gray-400 hover:text-red-500" title="Delete" data-testid={`delete-template-${t.id}`}>
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Template Detail / Field Mapping Panel */}
      {selectedTemplate && (
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="template-detail-panel">
          <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
            <h4 className="font-semibold text-gray-800">
              {selectedTemplate.file_type === 'pdf' ? 'Place Fields on PDF' : 'Field Placeholders for Word Template'}
              <span className="text-sm font-normal text-gray-500 ml-2">— {selectedTemplate.name}</span>
            </h4>
            <button onClick={() => { setSelectedTemplate(null); setPdfUrl(null); }} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
          </div>

          <div className="flex flex-col lg:flex-row" style={{ minHeight: 500 }}>
            {/* Field Catalog Sidebar */}
            <div className="lg:w-80 border-r border-gray-200 bg-white overflow-y-auto" style={{ maxHeight: 600 }} data-testid="field-catalog-panel">
              <div className="p-3 border-b sticky top-0 bg-white z-10">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
                  <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search fields..." className="w-full pl-8 pr-3 py-2 text-sm border rounded-lg" data-testid="field-search-input" />
                </div>
              </div>
              <div className="p-2">
                {filteredCatalog.map(section => {
                  const sKey = `${section.module}-${section.section}`;
                  const isExpanded = expandedSections[sKey] !== false;
                  return (
                    <div key={sKey} className="mb-1">
                      <button onClick={() => toggleSection(sKey)} className="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-gray-600 uppercase tracking-wider hover:bg-gray-50 rounded">
                        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        {section.module} &rarr; {section.section}
                      </button>
                      {isExpanded && (
                        <div className="ml-2 space-y-0.5">
                          {section.fields.map(field => (
                            <div key={field.key} className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-gray-50 group">
                              <div className="flex-1 min-w-0">
                                <span className="text-sm text-gray-800 block truncate">{field.label}</span>
                                <code className="text-xs text-gray-400">{`{{${field.key}}}`}</code>
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {selectedTemplate?.file_type === 'pdf' && (
                                  <button onClick={() => setPlacingField(field)}
                                    className={`p-1 rounded text-xs ${placingField?.key === field.key ? 'bg-blue-600 text-white' : 'text-blue-600 hover:bg-blue-50'}`}
                                    title="Place on PDF" data-testid={`place-field-${field.key}`}>
                                    <Eye size={12} />
                                  </button>
                                )}
                                <button onClick={() => copyPlaceholder(field.key)}
                                  className="p-1 text-gray-400 hover:text-blue-600 rounded" title="Copy tag"
                                  data-testid={`copy-field-${field.key}`}>
                                  {copiedField === field.key ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 p-4 overflow-auto bg-gray-100">
              {selectedTemplate.file_type === 'pdf' && pdfUrl ? (
                <>
                  {/* PDF Controls */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1} className="px-2 py-1 text-sm border rounded disabled:opacity-50">Prev</button>
                      <span className="text-sm text-gray-600">Page {currentPage} / {numPages}</span>
                      <button onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))} disabled={currentPage >= numPages} className="px-2 py-1 text-sm border rounded disabled:opacity-50">Next</button>
                    </div>
                    <div className="flex items-center gap-2">
                      {placingField && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          Click on PDF to place: <strong>{placingField.label}</strong>
                          <button onClick={() => setPlacingField(null)} className="ml-1 text-blue-500 hover:text-blue-700"><X size={10} /></button>
                        </span>
                      )}
                      <button onClick={savePdfPlacements} disabled={savingPlacements}
                        className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700" data-testid="save-pdf-placements-btn">
                        <Save size={14} className="mr-1" /> {savingPlacements ? 'Saving...' : 'Save Placements'}
                      </button>
                    </div>
                  </div>

                  {/* PDF Viewer with Overlays */}
                  <div ref={pdfContainerRef} className="relative bg-white rounded-lg shadow-sm inline-block cursor-crosshair" onClick={handlePdfClick} data-testid="pdf-viewer">
                    <Document file={pdfUrl} onLoadSuccess={({ numPages: n }) => setNumPages(n)} loading={<div className="p-12 text-gray-400">Loading PDF...</div>}>
                      <Page pageNumber={currentPage} width={600} />
                    </Document>
                    {/* Field placement overlays for current page */}
                    {pdfFieldPlacements.filter(p => p.page === currentPage).map((p, idx) => {
                      const globalIdx = pdfFieldPlacements.indexOf(p);
                      return (
                        <div key={globalIdx} className="absolute border-2 border-blue-500 bg-blue-100/60 rounded px-1 flex items-center group"
                          style={{ left: p.x, top: p.y, width: p.width, height: p.height, fontSize: p.font_size }}
                          data-testid={`pdf-field-${p.field_key}`}>
                          <span className="text-blue-800 text-xs font-medium truncate flex-1">{p.field_label}</span>
                          <button onClick={(e) => { e.stopPropagation(); removePdfPlacement(globalIdx); }}
                            className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 ml-1 flex-shrink-0"><X size={10} /></button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Placed fields summary */}
                  {pdfFieldPlacements.length > 0 && (
                    <div className="mt-3 p-3 bg-white rounded-lg border">
                      <h5 className="text-xs font-semibold text-gray-600 uppercase mb-2">Placed Fields ({pdfFieldPlacements.length})</h5>
                      <div className="flex flex-wrap gap-2">
                        {pdfFieldPlacements.map((p, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">
                            {p.field_label} (p.{p.page})
                            <button onClick={() => removePdfPlacement(idx)} className="text-blue-400 hover:text-red-500"><X size={10} /></button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Word Template Instructions */
                <div className="bg-white rounded-lg border p-6" data-testid="word-instructions">
                  <h4 className="font-semibold text-gray-800 mb-3">How to use placeholders in your Word template</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600 mb-4">
                    <li>Browse the <strong>Field Catalog</strong> on the left panel</li>
                    <li>Click the <Copy size={12} className="inline" /> copy icon next to any field to copy the placeholder tag</li>
                    <li>Open your Word template in Microsoft Word</li>
                    <li>Paste the placeholder tag (e.g. <code className="bg-gray-100 px-1 rounded text-xs">{'{{fund_name}}'}</code>) where you want the data to appear</li>
                    <li>Save and re-upload the updated template</li>
                  </ol>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-700">
                      <strong>Tip:</strong> Placeholders use double curly braces: <code className="bg-amber-100 px-1 rounded">{'{{field_key}}'}</code>. 
                      During document generation, these will be replaced with actual data from the selected records.
                    </p>
                  </div>

                  {/* Track which placeholders the user has copied */}
                  {selectedTemplate.placeholders?.length > 0 && (
                    <div className="mt-4">
                      <h5 className="text-xs font-semibold text-gray-600 uppercase mb-2">Mapped Placeholders ({selectedTemplate.placeholders.length})</h5>
                      <div className="flex flex-wrap gap-2">
                        {selectedTemplate.placeholders.map(key => (
                          <span key={key} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">
                            {`{{${key}}}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SOA Templates Section */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <SOATemplateDesigner fundId={fundId} canEdit={canEdit} />
      </div>
    </div>
  );
};

export default FundDocumentTemplates;
